/**
 * Known DKIM selectors, tagged by provider.
 *
 * Sources:
 * - Provider documentation (Google, Microsoft, SendGrid, Mailchimp, etc.)
 * - dkimscan public selector dictionary (github.com/ryancdotorg/dkimscan)
 * - Selectors observed in production DMARC aggregate reports
 *
 * Provider keys use a stable kebab-case form intentionally — the same key
 * is referenced by the tools-worker MX→provider map to drive targeted
 * probing, so changing one means updating both.
 */
/**
 * Provider-specific selectors. These are the ones we'd probe first when
 * MX tells us which mail platform is in use.
 *
 * Note: Amazon SES uses 32-char random selectors that are not predictable.
 * We list a couple of well-known shared keys and rely on report data for
 * tenant-specific ones.
 */
const PROVIDER_SELECTORS = [
    // Google Workspace — `google` is the legacy default; dated selectors
    // appear when admins rotate via the admin console. Google publishes new
    // dated selectors a few times per year; the list below covers the
    // common rotation windows we've observed.
    { selector: "google", provider: "google-workspace", priority: 1 },
    { selector: "google2", provider: "google-workspace", priority: 2 },
    { selector: "20230601", provider: "google-workspace", priority: 3 },
    { selector: "20221208", provider: "google-workspace", priority: 3 },
    { selector: "20210112", provider: "google-workspace", priority: 4 },
    { selector: "20161025", provider: "google-workspace", priority: 5 },
    // Microsoft 365 — selector1/selector2 are universal across all tenants.
    // selector1-<tenant> and selector2-<tenant> variants exist on custom
    // domains but the bare form is canonical.
    { selector: "selector1", provider: "microsoft-365", priority: 1 },
    { selector: "selector2", provider: "microsoft-365", priority: 1 },
    // SendGrid (Twilio) — s1/s2 are the modern defaults, smtpapi is legacy.
    // Customer-specific em#### selectors exist on dedicated IPs but we
    // can't predict the digits.
    { selector: "s1", provider: "sendgrid", priority: 1 },
    { selector: "s2", provider: "sendgrid", priority: 1 },
    { selector: "smtpapi", provider: "sendgrid", priority: 2 },
    { selector: "sendgrid", provider: "sendgrid", priority: 3 },
    // Mailchimp / Mandrill
    { selector: "k1", provider: "mailchimp", priority: 1 },
    { selector: "k2", provider: "mailchimp", priority: 2 },
    { selector: "k3", provider: "mailchimp", priority: 3 },
    { selector: "mandrill", provider: "mailchimp", priority: 2 },
    { selector: "mte1", provider: "mailchimp", priority: 4 },
    { selector: "mte2", provider: "mailchimp", priority: 4 },
    // Amazon SES — random hashes are unguessable; only worth probing the
    // shared/legacy selector.
    { selector: "amazonses", provider: "amazon-ses", priority: 1 },
    { selector: "ses", provider: "amazon-ses", priority: 2 },
    // Postmark
    { selector: "pm", provider: "postmark", priority: 1 },
    { selector: "20200924102846pm", provider: "postmark", priority: 3 },
    // Mailgun — uses sub-domain selectors like mta._domainkey.mg.example.com
    // but on the root domain the common selectors are these.
    { selector: "mailo", provider: "mailgun", priority: 1 },
    { selector: "smtp", provider: "mailgun", priority: 2 },
    { selector: "mailgun", provider: "mailgun", priority: 2 },
    { selector: "mta", provider: "mailgun", priority: 3 },
    { selector: "krs", provider: "mailgun", priority: 3 },
    // SparkPost (MessageBird)
    { selector: "scph0717", provider: "sparkpost", priority: 1 },
    { selector: "scph0817", provider: "sparkpost", priority: 1 },
    { selector: "scph0920", provider: "sparkpost", priority: 1 },
    { selector: "sparkpost", provider: "sparkpost", priority: 2 },
    // Mailjet
    { selector: "mailjet", provider: "mailjet", priority: 1 },
    // Brevo (formerly Sendinblue)
    { selector: "mail", provider: "brevo", priority: 2 },
    { selector: "sib", provider: "brevo", priority: 1 },
    // Salesforce (Marketing Cloud + core)
    { selector: "sf", provider: "salesforce", priority: 1 },
    { selector: "salesforce", provider: "salesforce", priority: 2 },
    { selector: "200608", provider: "salesforce", priority: 3 },
    { selector: "et", provider: "salesforce", priority: 2 },
    // Marketo
    { selector: "m1", provider: "marketo", priority: 1 },
    { selector: "mailer", provider: "marketo", priority: 2 },
    { selector: "mtad", provider: "marketo", priority: 2 },
    { selector: "mta1", provider: "marketo", priority: 2 },
    // Pardot
    { selector: "pardot", provider: "pardot", priority: 1 },
    // HubSpot — uses hs1-<accountid> / hs2-<accountid>; we list the bare
    // prefixes since the suffix is per-account.
    { selector: "hs1", provider: "hubspot", priority: 1 },
    { selector: "hs2", provider: "hubspot", priority: 1 },
    { selector: "hubspot", provider: "hubspot", priority: 2 },
    // Zendesk
    { selector: "zendesk1", provider: "zendesk", priority: 1 },
    { selector: "zendesk2", provider: "zendesk", priority: 1 },
    // Intercom
    { selector: "intercom", provider: "intercom", priority: 1 },
    // Freshdesk / Freshworks
    { selector: "freshdesk1", provider: "freshdesk", priority: 1 },
    { selector: "freshdesk2", provider: "freshdesk", priority: 1 },
    { selector: "fd", provider: "freshdesk", priority: 2 },
    // Help Scout
    { selector: "helpscout", provider: "helpscout", priority: 1 },
    // Klaviyo
    { selector: "klaviyo", provider: "klaviyo", priority: 1 },
    { selector: "klaviyo2", provider: "klaviyo", priority: 2 },
    { selector: "kl", provider: "klaviyo", priority: 2 },
    // Constant Contact
    { selector: "ctct1", provider: "constant-contact", priority: 1 },
    { selector: "ctct2", provider: "constant-contact", priority: 1 },
    // ActiveCampaign
    { selector: "dk", provider: "activecampaign", priority: 1 },
    { selector: "ac", provider: "activecampaign", priority: 2 },
    // Drip
    { selector: "drip", provider: "drip", priority: 1 },
    // Iterable
    { selector: "iterable1", provider: "iterable", priority: 1 },
    { selector: "iterable2", provider: "iterable", priority: 1 },
    // Customer.io
    { selector: "customerio", provider: "customer-io", priority: 1 },
    { selector: "cio", provider: "customer-io", priority: 2 },
    // Front
    { selector: "front", provider: "front", priority: 1 },
    { selector: "frontapp", provider: "front", priority: 2 },
    // Mixmax
    { selector: "mixmax", provider: "mixmax", priority: 1 },
    // Yesware
    { selector: "yesware", provider: "yesware", priority: 1 },
    // Loops
    { selector: "loops", provider: "loops", priority: 1 },
    // Resend
    { selector: "resend", provider: "resend", priority: 1 },
    // Fastmail
    { selector: "fm1", provider: "fastmail", priority: 1 },
    { selector: "fm2", provider: "fastmail", priority: 1 },
    { selector: "fm3", provider: "fastmail", priority: 1 },
    { selector: "mesmtp", provider: "fastmail", priority: 2 },
    // Zoho
    { selector: "zoho", provider: "zoho", priority: 1 },
    { selector: "zmail", provider: "zoho", priority: 2 },
    // ProtonMail
    { selector: "protonmail", provider: "protonmail", priority: 1 },
    { selector: "protonmail2", provider: "protonmail", priority: 2 },
    { selector: "protonmail3", provider: "protonmail", priority: 2 },
    // Apple iCloud
    { selector: "sig1", provider: "apple-icloud", priority: 1 },
    // Mimecast (sometimes signs as the sending platform)
    { selector: "mimecast", provider: "mimecast", priority: 1 },
    { selector: "mimecast20231214", provider: "mimecast", priority: 2 },
    // Proofpoint
    { selector: "pps", provider: "proofpoint", priority: 1 },
    { selector: "proofpoint", provider: "proofpoint", priority: 2 },
];
/**
 * Generic/fallback selectors not tied to a single provider. Probed when
 * MX doesn't match a known provider, or after provider-targeted probing
 * fails. Ordered roughly by observed frequency.
 */
const GENERIC_SELECTORS = [
    { selector: "default", priority: 1 },
    { selector: "dkim", priority: 1 },
    { selector: "mail", priority: 1 },
    { selector: "email", priority: 2 },
    { selector: "key1", priority: 2 },
    { selector: "key2", priority: 2 },
    { selector: "selector", priority: 2 },
    { selector: "smtp", priority: 3 },
    { selector: "mx", priority: 3 },
    { selector: "main", priority: 3 },
    { selector: "primary", priority: 3 },
    { selector: "dkim1", priority: 3 },
    { selector: "dkim2", priority: 3 },
    { selector: "private", priority: 4 },
    { selector: "alpha", priority: 4 },
    { selector: "beta", priority: 4 },
    { selector: "gamma", priority: 4 },
    { selector: "delta", priority: 4 },
    { selector: "prod", priority: 4 },
    { selector: "test", priority: 5 },
    { selector: "auth", priority: 4 },
    { selector: "outbound", priority: 4 },
    { selector: "domk", priority: 5 },
    { selector: "centralsmtp", priority: 5 },
    { selector: "dk", priority: 4 },
];
export const COMMON_SELECTORS = [
    ...PROVIDER_SELECTORS,
    ...GENERIC_SELECTORS,
];
/**
 * Plain string-array form for callers that only need names (e.g. the
 * old tools-worker checker). Order matches COMMON_SELECTORS.
 */
export const COMMON_SELECTOR_NAMES = COMMON_SELECTORS.map((s) => s.selector);
/** Selectors sorted globally by priority (lowest first). */
export const SELECTORS_BY_PRIORITY = [...COMMON_SELECTORS].sort((a, b) => a.priority - b.priority);
/**
 * Curated short list used for the blind-probe fallback when MX doesn't
 * match a known provider. Kept tight to bound DNS subrequests per
 * request — firing 100+ parallel DoH calls is rude to the resolver and
 * burns through Cloudflare Workers' subrequest budget.
 *
 * The selectors here cover ~90% of domains that have any DKIM at all,
 * based on observed selector distribution. Anything outside this set is
 * better discovered from real DMARC reports (where the selector is
 * named in the auth_results) than guessed at.
 *
 * Keep this list well under 30 entries.
 */
export const BROAD_PROBE_SELECTORS = [
    // Generic — almost everyone with custom DKIM uses one of these
    "default",
    "dkim",
    "mail",
    "email",
    "key1",
    // Microsoft 365 — universal across all tenants
    "selector1",
    "selector2",
    // Google Workspace — canonical default
    "google",
    // SendGrid (Twilio)
    "s1",
    "s2",
    "smtpapi",
    // Mailchimp / Mandrill
    "k1",
    "k2",
    "mandrill",
    // Postmark
    "pm",
    // Amazon SES (shared/legacy only — tenant-specific hashes are unguessable)
    "amazonses",
    // HubSpot
    "hs1",
    "hs2",
    // Salesforce
    "sf",
    // Zendesk
    "zendesk1",
    "zendesk2",
    // Klaviyo
    "klaviyo",
    // Mailgun
    "mailo",
    "mta",
];
/**
 * Find a known selector entry by selector name (case-insensitive).
 * Useful for tagging report-discovered selectors with a provider hint.
 */
export function findKnownSelector(selector) {
    const target = selector.toLowerCase();
    return COMMON_SELECTORS.find((s) => s.selector.toLowerCase() === target);
}
/**
 * Get the selectors associated with a specific provider, ordered by
 * priority (lowest first). Returns [] if the provider key is unknown.
 */
export function getSelectorsForProvider(provider) {
    return PROVIDER_SELECTORS.filter((s) => s.provider === provider).sort((a, b) => a.priority - b.priority);
}
/** All generic (provider-less) selectors, ordered by priority. */
export const GENERIC_SELECTOR_LIST = [...GENERIC_SELECTORS].sort((a, b) => a.priority - b.priority);
//# sourceMappingURL=dkim-selectors.js.map